import { FieldOptionBase } from 'module/api/field/types';
import { useTranslation } from 'module/hooks/use-translation';
import { Loading } from 'module/ui/components/atoms/loading';
import { CustomSectionState } from 'api/custom-section/types';
import { CustomSectionAttributes } from 'api/custom-section/types';
import { useMemo, useState, useCallback } from 'react';
import {
  useAccountFields,
  useInitialPromptFields,
  DetailBlockPromptState,
  constructBlockFieldsOptions,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/detail-block-utils';
import {
  SectionPreview,
  SectionTitle,
  SectionTableContainer,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { SharedDetailBlockPromptInput } from './prompt-input';
import { SharedDetailBlockComponentProps } from './types';
import { FieldsTable } from '../../shared/fields-table';
import { filterableFieldsFromFieldNames } from '../../shared/response-renderer-utils';

export const SharedDetailBlockPromptSection = <
  T extends CustomSectionAttributes,
>({
  node,
  sourceType,
  onOpen,
  useFieldsHook,
  defaultTitleKey,
  titlePlaceholderKey,
  fieldLabelKey,
  onDone,
  onUndo,
  contentEditable,
  onRemove,
  allowResolve,
  pageType,
  pageTriggerListFilter,
}: SharedDetailBlockComponentProps<T>) => {
  const showPrompt = (node.attrs as any).state === CustomSectionState.Open;
  const { fields: sourceFields, loading: isLoading } = useFieldsHook();
  const [isGenerating, setIsGenerating] = useState(false);

  const sourceFieldsMapped = useAccountFields(sourceFields, isLoading);
  const [prompt, setPrompt] = useState<DetailBlockPromptState>({
    fields: [],
    title: (node.attrs as any).title || '',
  });

  useInitialPromptFields(
    isLoading,
    sourceFieldsMapped,
    (node.attrs as any).config?.fields || [],
    prompt,
    setPrompt,
  );

  const configFields = useMemo(
    () => (node.attrs as any).config?.fields || [],
    [node.attrs],
  );

  const configuredFields = useMemo(
    () =>
      filterableFieldsFromFieldNames(
        configFields,
        sourceFields ?? [],
        [],
        sourceType,
      ),
    [sourceFields, configFields, sourceType],
  );

  const handleGenerate = async (userPrompt: {
    fields: FieldOptionBase<any>[];
    title: string;
  }) => {
    if (!onDone) {
      return;
    }

    setIsGenerating(true);
    const attributes = {
      ...node.attrs,
      title: userPrompt.title,
      config: { fields: userPrompt.fields.map((field) => field.value) },
      state: CustomSectionState.Closed,
    } as T;

    onDone(attributes).then(() => {
      setIsGenerating(false);
    });
  };

  const onClose = useCallback(() => {
    setPrompt({
      fields: constructBlockFieldsOptions(configFields, sourceFieldsMapped),
      title: (node.attrs as any).title || '',
    });
    onUndo?.();
  }, [onUndo, node.attrs, configFields, sourceFieldsMapped]);

  const defaultTitle = useTranslation(defaultTitleKey as any);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <SharedDetailBlockPromptInput
          prompt={prompt}
          defaultTitleKey={defaultTitleKey}
          setPrompt={setPrompt}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
          fields={sourceFieldsMapped}
          onCancel={onRemove || (() => {})}
          onUndo={onClose}
          allowResolve={allowResolve}
          titlePlaceholderKey={titlePlaceholderKey}
          fieldLabelKey={fieldLabelKey}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      ) : (
        <SectionPreview onClick={() => onOpen?.()} isLoading={isLoading}>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <SectionTitle>{prompt.title || defaultTitle}</SectionTitle>
              <SectionTableContainer>
                <FieldsTable fields={configuredFields} responsesLookup={{}} />
              </SectionTableContainer>
            </>
          )}
        </SectionPreview>
      )}
    </div>
  );
};
